import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { Activity, getActivityActionText } from 'types/interfaces/activity';
import { formatRelativeTime, formatAccessibleTime } from 'utils/relativeTime';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { FoilCardImage } from 'components/FoilCardImage/FoilCardImage';
import { Points } from 'components/Points/Points';
import { trackEvent } from 'utils/analytics';
import './ActivityCard.css';

interface ActivityCardProps {
  activity: Activity;
  position?: number;
  isNew?: boolean;
}

/**
 * ActivityCard - Displays a single activity in the carousel
 * Shows: avatar, username, action, card image, point value, timestamp
 */
export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  position = 0,
  isNew = false,
}) => {
  const navigate = useNavigate();

  // Handle card click - navigate to card detail
  const handleCardClick = useCallback(() => {
    trackEvent('carousel_card_clicked', {
      activity_type: activity.type,
      card_id: activity.card.id,
      card_name: activity.card.name,
      position,
    });
    navigate(`/cards/${activity.card.id}`);
  }, [activity, position, navigate]);

  // Handle username/avatar click - track analytics
  const handleUserClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      trackEvent('carousel_username_clicked', {
        user_id: activity.user.id,
        username: activity.user.username,
        activity_type: activity.type,
      });
    },
    [activity]
  );

  // Handle card image error - try fallbacks then placeholder
  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const currentSrc = img.src;
      const scryfallId = activity.card.scryfallId;
      const imageComingSoon = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/Search/image-coming-soon.png';

      // Fallback 1: Try large size from CDN
      if (!img.dataset.triedLarge && currentSrc.includes('/normal/')) {
        img.dataset.triedLarge = 'true';
        const largeUrl = currentSrc.replace('/normal/', '/large/');
        img.src = largeUrl;
        return;
      }

      // Fallback 2: Try small size from CDN
      if (!img.dataset.triedSmall && (currentSrc.includes('/large/') || currentSrc.includes('/normal/'))) {
        img.dataset.triedSmall = 'true';
        const smallUrl = currentSrc.replace('/large/', '/small/').replace('/normal/', '/small/');
        img.src = smallUrl;
        return;
      }

      // Fallback 3: Try Scryfall direct (staging environment)
      if (process.env.REACT_APP_ENV === 'staging' && !img.dataset.triedScryfall && scryfallId) {
        img.dataset.triedScryfall = 'true';
        const scryfallUrl = `https://cards.scryfall.io/normal/front/${scryfallId.slice(0, 1)}/${scryfallId.slice(1, 2)}/${scryfallId}.jpg`;
        img.src = scryfallUrl;
        return;
      }

      // Final fallback: "Image coming soon" placeholder
      if (img.src !== imageComingSoon) {
        img.src = imageComingSoon;
      }
    },
    [activity.card.scryfallId]
  );

  const actionText = getActivityActionText(activity.type);

  return (
    <div
      className={`activity-card ${isNew ? 'activity-card--new' : ''}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick();
        }
      }}
      aria-label={`${activity.user.username} ${actionText} ${activity.card.name}`}
    >
      {/* User info header */}
      <Link
        to={`/profile/u/${encodeURIComponent(activity.user.username)}`}
        className="activity-card__header"
        onClick={handleUserClick}
      >
        <div className="activity-card__avatar">
          <AvatarWithFlag
            avatarUrl={activity.user.avatarUrl}
            username={activity.user.username}
            countryCode={activity.user.countryCode}
            size={30}
            isElite={activity.user.isElite}
            showAnimatedBorder={activity.user.showAnimatedBorder}
            borderColor1={activity.user.borderColor1}
            borderColor2={activity.user.borderColor2}
          />
        </div>
        <div className="activity-card__user-info">
          <span className="activity-card__username">{activity.user.username}</span>
          <span className="activity-card__action">{actionText}</span>
        </div>
      </Link>

      {/* Card image */}
      <div className="activity-card__image-container">
        <FoilCardImage
          src={activity.card.imageUrl}
          alt={activity.card.name}
          foil={activity.card.foil}
          loading="lazy"
          onError={handleImageError}
          className="activity-card__image"
          borderRadius="10px"
        />
      </div>

      {/* Point value and timestamp */}
      <div className="activity-card__footer">
        {activity.pointValue !== null ? (
          <Points points={activity.pointValue} type="small" />
        ) : (
          <span className="activity-card__no-price">???</span>
        )}
        <time
          className="activity-card__timestamp"
          dateTime={activity.createdAt}
          title={formatAccessibleTime(activity.createdAt)}
        >
          {formatRelativeTime(activity.createdAt)}
        </time>
      </div>
    </div>
  );
};

export default ActivityCard;
